import { View } from 'react-native';
import { style } from './TooltipSaveProject.style';

import { TestIDs } from 'src/constants/testIDs.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import { useStore } from 'src/contexts/StoreContext';
import TextLato from '../text/text-lato/TextLato';
import { useState } from 'react';
import { brand, neutrals } from 'src/theme/colors';
import { SAVING_STATE } from 'src/store/page-project/pageProject.constants';
import { getTimeAndDateInfo } from 'src/helpers/time.helper';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { observer } from 'mobx-react-lite';
import { BaseTextInput } from '../text-input/base-text-input/BaseTextInput';

const TITLE_MAX_LENGHT = 32;

export const TooltipSaveProject = observer(({ onClose }: { onClose: () => void }) => {
  const {
    i18n,
    project: { activeProject },
  } = useStore();
  const [projectTitle, setProjectTitle] = useState<string>(activeProject?.name ?? '');

  const setProjectName = useDebounceCallback((text: string) => {
    activeProject?.setName(text);
  }, 1000);

  const handleNewTitle = (text: string) => {
    if (text.length <= TITLE_MAX_LENGHT) {
      setProjectTitle(text);
      setProjectName(text);
    }
  };

  const TooltipContent = () => {
    const timeAndDateInfo = getTimeAndDateInfo(activeProject!.lastSaveTimestamp);
    switch (activeProject?.savingState) {
      case SAVING_STATE.saved:
        return (
          <View style={style.topContent}>
            <TextLato style={style.title}>{i18n.t('tooltip-save-project:auto-saved')}</TextLato>
            {timeAndDateInfo && (
              <TextLato style={style.description}>
                {i18n.t('tooltip-save-project:last-save_date_hour', timeAndDateInfo)}
              </TextLato>
            )}
          </View>
        );
      case SAVING_STATE.saving:
        return (
          <View style={[style.topContent, { gap: 16 }]}>
            <TextLato style={style.title}>{i18n.t('tooltip-save-project:saving')}</TextLato>
            {/* PROGRESS BAR NOT PRESENT IN FIRST RELEASE */}
            {/* <View style={style.progressBar}>
              <View style={[style.progressBarContent, { width: '60%' }]}>
                <LinearGradient
                  colors={[brand.main.iris, brand.main.hibiscus, brand.main.orange]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={style.progressBarGradient}
                >
                  <TextLato style={style.progressBarText}>60%</TextLato>
                </LinearGradient>
              </View>
            </View> */}
          </View>
        );
      default:
        return <></>;
    }
  };

  return (
    <View style={style.tooltip}>
      <View style={style.content}>
        <TooltipContent />
        <View style={style.bottomContent}>
          <TextLato style={style.subtitle}>{i18n.t('tooltip-save-project:title')}</TextLato>
          <BaseTextInput
            style={style.input}
            focusStyle={style.inputFocus}
            inputStyle={style.inputText}
            onChangeText={text => handleNewTitle(text)}
            value={projectTitle}
            placeholder={i18n.t('tooltip-save-project:placeholder')}
            placeholderTextColor={neutrals.neutralGrays.grey}
            focusId={modalTestID(TestIDs.TOOLTIP_SAVE_PROJECT, TestIDs.TITLE)}
            focusOrder={0}
            focusGroup={FocusGroups.TOOLTIP}
          >
            {() => (
              <TextLato style={style.inputCounter}>
                {projectTitle.length}/{TITLE_MAX_LENGHT}
              </TextLato>
            )}
          </BaseTextInput>
        </View>
      </View>
      <CircularButton
        onPress={async (...args) => onClose(...args)}
        accessible={true}
        accessibilityLabel={i18n.t(`actions:close_x`, { x: i18n.t(`general:modal`) })}
        accessibilityRole="button"
        testID={modalTestID(TestIDs.TOOLTIP_SAVE_PROJECT, TestIDs.CLOSE)}
        iconName={ICON_NAMES.Close}
        iconStyle={style.closeIcon}
        size={CIRCULAR_BUTTON_SIZES.SMALL}
        type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
        focusOrder={1}
        focusGroup={FocusGroups.TOOLTIP}
        focusId={modalTestID(TestIDs.TOOLTIP_SAVE_PROJECT, TestIDs.CLOSE)}
      />
    </View>
  );
});
